import React, { useState, useEffect } from 'react';
import {
    Target, Shield, BookOpen, Heart, Activity,
    Zap, Eye, Users, ChevronRight, CheckCircle2,
    Calendar, ListTodo, Sun, DollarSign, Briefcase,
    Milestone, Trophy, ListChecks, Quote, Star, Clock
} from 'lucide-react';
import { config } from '../config';

const LifeOS = ({ user, userMetadata }: { user: any; userMetadata: Record<string, any> | null }) => {
    const [activeLevel, setActiveLevel] = useState(0); // 0: Today, 1: Weekly/Monthly, etc.
    const [activeArea, setActiveArea] = useState('All');
    const [loading, setLoading] = useState(true);
    const [dailyPlan, setDailyPlan] = useState<{
        planMetadata?: {
            memoryScripture?: string;
        };
        events?: {
            start: string;
            end: string;
            title: string;
            type: string;
        }[];
        routine?: {
            Time: string;
            Task: string;
            Category: string;
            Done: boolean;
        }[];
        tasks?: {
            title: string;
            category: string;
            completed: boolean;
        }[];
    } | null>(null);
    const [horizons, setHorizons] = useState<{
        Horizon: number;
        Title: string;
        LifeArea: string;
        SuccessLaw: string;
        progress: number;
    }[]>([]);

    const lifeAreas = [
        { id: 'Spiritual', icon: <Sun size={18} />, color: 'orange' },
        { id: 'Material', icon: <Heart size={18} />, color: 'rose' },
        { id: 'Financial', icon: <DollarSign size={18} />, color: 'emerald' },
        { id: 'Business', icon: <Briefcase size={18} />, color: 'blue' }
    ];

    const horizonsList = [
        { level: 6, title: 'STRATEGIC VISION', sub: 'Purpose of Life' },
        { level: 5, title: '1-3 YEAR GOALS', sub: 'Long-term Growth' },
        { level: 4, title: 'ANNUAL PLAN', sub: 'Yearly Focus' },
        { level: 3, title: 'QUARTERLY', sub: 'Milestones' },
        { level: 2, title: 'MONTHLY PLAN', sub: 'Sprints' },
        { level: 1, title: 'WEEKLY PLAN', sub: 'Process & Habits' },
        { level: 0, title: 'TODAY', sub: 'Daily Execution' }
    ];

    useEffect(() => {
        fetchData();
    }, [activeLevel, userMetadata?.email]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch Daily Plan if activeLevel is 0
            if (activeLevel === 0) {
                const res = await fetch(`${config.API_BASE_URL}/webhook/daily-plan?UserID=${userMetadata?.email || user.email}&Date=${new Date().toISOString().split('T')[0]}`);
                const data = await res.json();
                setDailyPlan(data);
            }
            // Fetch Horizons summary
            const hRes = await fetch(`${config.API_BASE_URL}/webhook/productivity-sync`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ UserID: userMetadata?.email || user.email })
            });
            const hData = await hRes.json();
            setHorizons(hData.updatedHorizons || []);
        } catch (error) {
            console.error('Error fetching productivity data:', error);
        }
        setLoading(false);
    };

    const renderDayView = () => (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Trial Timer Widget (if applicable) */}
            {userMetadata?.Trial_Start_Date && (
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2rem] p-6 text-white shadow-xl shadow-blue-100 flex justify-between items-center">
                    <div>
                        <div className="flex items-center gap-2 mb-1 opacity-80">
                            <Clock size={16} /> <span className="text-[10px] font-black uppercase tracking-widest">Trial Access</span>
                        </div>
                        <h2 className="text-2xl font-black">
                            {(() => { const start = new Date(userMetadata.Trial_Start_Date).getTime(); const end = start + (14 * 24 * 60 * 60 * 1000); return Math.max(0, Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24))); })()} Days Left
                        </h2>
                    </div>
                    <button className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-xs font-bold transition-all border border-white/10">
                        Upgrade Now
                    </button>
                </div>
            )}

            {/* Spiritual & Mental Header */}
            <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-[2rem] p-6 text-white shadow-xl shadow-orange-100">
                <div className="flex items-center gap-2 mb-4">
                    <Sun size={20} className="text-orange-100" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-orange-100">Morning Alignment</span>
                </div>
                <div className="space-y-4">
                    <div>
                        <h3 className="text-[10px] font-bold uppercase tracking-tighter opacity-80">Memory Scripture</h3>
                        <p className="text-sm font-medium italic mt-1 leading-relaxed">
                            {dailyPlan?.planMetadata?.memoryScripture || "Set your heart on things above..."}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-[10px] font-bold uppercase tracking-tighter opacity-80">Daily Gratitude</h3>
                        <div className="flex items-center gap-2 mt-2">
                            <Star size={14} className="text-amber-200 fill-amber-200" />
                            <span className="text-xs font-medium">Thankful for the gift of a new day and purposeful work.</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Schedule & Meetings */}
            <div className="bg-white/70 backdrop-blur-md rounded-[2rem] p-6 border border-white/40 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-100 p-2 rounded-xl text-indigo-700">
                            <Calendar size={20} />
                        </div>
                        <div>
                            <h2 className="text-sm font-black text-slate-800 tracking-tight uppercase">Upcoming Meetings</h2>
                            <p className="text-[10px] text-slate-400 font-bold tracking-widest uppercase">GCal Sync Active</p>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                                    {dailyPlan && dailyPlan.events && dailyPlan.events.length > 0 ? (
                                        dailyPlan.events.map((event, idx) => (
                                            <div key={idx} className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 flex items-center justify-between group cursor-pointer hover:bg-indigo-50 transition-all">
                                                <div className="flex items-center gap-4">
                                                    <div className="text-center w-8">
                                                        <span className="block text-[10px] font-black text-indigo-400 uppercase">
                                                            {new Date(event.start).toLocaleString('en-US', { month: 'short' })}
                                                        </span>
                                                        <span className="block text-lg font-black text-indigo-700 leading-none">
                                                            {new Date(event.start).getDate()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <h4 className="text-xs font-bold text-slate-800">{event.title}</h4>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Clock size={10} className="text-slate-400" />
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                                                                {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {event.type}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button className="p-2 bg-white rounded-xl text-indigo-600 shadow-sm opacity-0 group-hover:opacity-100 transition-all">
                                                    <ChevronRight size={16} />
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 opacity-40">
                                            <Calendar size={32} className="mx-auto mb-2" />
                                            <p className="text-[10px] font-bold uppercase tracking-widest">No meetings scheduled</p>
                                        </div>
                                    )}
                                </div>
            </div>

            {/* Daily Routine Loader */}
            <div className="bg-white/70 backdrop-blur-md rounded-[2rem] p-6 border border-white/40 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-xl text-blue-700">
                            <ListChecks size={20} />
                        </div>
                        <div>
                            <h2 className="text-sm font-black text-slate-800 tracking-tight uppercase">Daily Routine</h2>
                            <p className="text-[10px] text-slate-400 font-bold tracking-widest uppercase">Standard Work Day</p>
                        </div>
                    </div>
                    <button className="text-[10px] bg-slate-100 px-3 py-1.5 rounded-full font-black text-slate-500 hover:bg-slate-200 transition-colors uppercase">
                        Load Template
                    </button>
                </div>

                <div className="space-y-3">
                    {dailyPlan?.routine?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 rounded-2xl hover:bg-white/50 transition-all group cursor-pointer border border-transparent hover:border-slate-100">
                            <div className="text-[10px] font-black text-slate-300 w-12 tabular-nums">{item.Time}</div>
                            <div className="flex-1">
                                <h4 className="text-xs font-bold text-slate-700">{item.Task}</h4>
                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">{item.Category}</span>
                            </div>
                            <div className={`w-5 h-5 rounded-full border-2 transition-all flex items-center justify-center ${item.Done ? 'bg-green-500 border-green-500' : 'border-slate-200 group-hover:border-blue-400'}`}>
                                {item.Done && <CheckCircle2 size={14} className="text-white" />}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Power List (Priority Tasks) */}
            <div className="bg-white/70 backdrop-blur-md rounded-[2rem] p-6 border border-white/40 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-rose-100 p-2 rounded-xl text-rose-700">
                        <Target size={20} />
                    </div>
                    <div>
                        <h2 className="text-sm font-black text-slate-800 tracking-tight uppercase">Power List</h2>
                        <p className="text-[10px] text-slate-400 font-bold tracking-widest uppercase text-rose-500">Must Achieve Today</p>
                    </div>
                </div>

                <div className="space-y-4">
                                    {dailyPlan && dailyPlan.tasks && dailyPlan.tasks.length > 0 ? (
                                        dailyPlan.tasks.map((task, idx) => (
                                            <div key={idx} className="flex items-center gap-4 p-4 bg-white/80 rounded-2xl border border-slate-100 shadow-sm group hover:border-blue-200 transition-all">
                                                <div className="bg-rose-50 text-rose-600 font-black text-xs w-6 h-6 flex items-center justify-center rounded-lg">{idx + 1}</div>
                                                <div className="flex-1">
                                                    <p className="text-xs font-bold text-slate-700">{task.title}</p>
                                                    <span className="text-[8px] text-slate-400 font-black uppercase tracking-widest">{task.category}</span>
                                                </div>
                                                <div className="w-5 h-5 rounded-full border-2 border-slate-200 group-hover:border-blue-400 flex items-center justify-center transition-all">
                                                    <Zap size={10} className="text-slate-200 group-hover:text-blue-400" />
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 opacity-40">
                                            <Target size={32} className="mx-auto mb-2" />
                                            <p className="text-[10px] font-bold uppercase tracking-widest">All clear for today!</p>
                                        </div>
                                    )}
                                </div>
            </div>

            {/* Pressing Needs & Metrics */}
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-600 rounded-[2rem] p-5 text-white shadow-xl shadow-blue-100">
                    <h3 className="text-[9px] font-black uppercase tracking-widest text-blue-200 mb-2">Metrics/KPIs</h3>
                    <p className="text-lg font-black tracking-tighter">78%</p>
                    <div className="w-full bg-blue-400/30 h-1.5 rounded-full mt-2 overflow-hidden">
                        <div className="bg-white h-full w-[78%]" />
                    </div>
                </div>
                <div className="bg-slate-800 rounded-[2rem] p-5 text-white shadow-xl shadow-slate-200">
                    <h3 className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Pressing Needs</h3>
                    <p className="text-xs font-medium leading-tight">Server Migration & Q4 Billing sync</p>
                </div>
            </div>
        </div>
    );

    const renderHorizonView = () => (
        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
            {horizonsList.find(h => h.level === activeLevel)?.sub && (
                <div className="px-2 mb-4">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">Active Horizon</p>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight leading-none mt-1">
                        {horizonsList.find(h => h.level === activeLevel)?.title}
                    </h2>
                </div>
            )}

            {horizons.filter(h => Number(h.Horizon) === activeLevel).map((h, idx) => (
                <div key={idx} className="bg-white/70 backdrop-blur-md rounded-[2.5rem] p-6 border border-white/40 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="bg-slate-100 p-2.5 rounded-2xl text-slate-700">
                                <Milestone size={24} />
                            </div>
                            <div>
                                <h3 className="text-base font-black text-slate-800 tracking-tight leading-tight">{h.Title}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${h.LifeArea === 'Business' ? 'bg-blue-100 text-blue-600' :
                                        h.LifeArea === 'Spiritual' ? 'bg-orange-100 text-orange-600' :
                                            'bg-slate-100 text-slate-600'
                                        }`}>
                                        {h.LifeArea}
                                    </span>
                                    <span className="text-[9px] font-bold text-slate-400 uppercase">Law: {h.SuccessLaw}</span>
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-lg font-black text-slate-800 leading-none">{h['Progress%']}%</span>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mt-1">Complete</p>
                        </div>
                    </div>

                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div
                            className={`h-full transition-all duration-1000 ${h['Progress%'] > 75 ? 'bg-green-500' : h['Progress%'] > 25 ? 'bg-blue-500' : 'bg-slate-400'
                                }`}
                            style={{ width: `${h['Progress%']}%` }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="flex flex-col min-h-full pb-24">
            {/* Life Area Filters */}
            <div className="flex gap-2 overflow-x-auto px-4 pb-4 no-scrollbar">
                <button
                    onClick={() => setActiveArea('All')}
                    className={`flex-shrink-0 px-4 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${activeArea === 'All' ? 'bg-slate-800 text-white shadow-lg' : 'bg-white/50 text-slate-500'
                        }`}
                >
                    All Areas
                </button>
                {lifeAreas.map(area => (
                    <button
                        key={area.id}
                        onClick={() => setActiveArea(area.id)}
                        className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${activeArea === area.id
                            ? `bg-${area.color}-600 text-white shadow-lg shadow-${area.color}-100`
                            : 'bg-white/50 text-slate-500'
                            }`}
                    >
                        {area.icon}
                        {area.id}
                    </button>
                ))}
            </div>

            {/* Layout Wrapper */}
            <div className="flex gap-6 px-4 flex-col lg:flex-row">
                {/* Side Horizon Navigation */}
                <div className="lg:w-72 space-y-2">
                    {horizonsList.map(h => (
                        <button
                            key={h.level}
                            onClick={() => setActiveLevel(h.level)}
                            className={`w-full flex items-center gap-4 p-4 rounded-[1.8rem] transition-all duration-300 text-left ${activeLevel === h.level
                                ? 'bg-white shadow-xl shadow-slate-100 border-l-4 border-blue-600 scale-[1.02]'
                                : 'bg-white/30 hover:bg-white/50'
                                }`}
                        >
                            <div className={`p-2 rounded-xl ${activeLevel === h.level ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                                {h.level === 0 ? <Activity size={18} /> : <Target size={18} />}
                            </div>
                            <div>
                                <h3 className={`text-[11px] font-black uppercase tracking-tighter ${activeLevel === h.level ? 'text-slate-800' : 'text-slate-400'}`}>
                                    {h.title}
                                </h3>
                                <p className="text-[9px] font-bold text-slate-400 mt-0.5 tracking-tight">{h.sub}</p>
                            </div>
                            {activeLevel === h.level && <ChevronRight size={14} className="ml-auto text-blue-400" />}
                        </button>
                    ))}
                </div>

                {/* Main Content Area */}
                <div className="flex-1">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center p-20 text-slate-300">
                            <div className="animate-spin mb-4"><Zap size={32} /></div>
                            <p className="text-xs font-black uppercase tracking-widest">Loading Horizons...</p>
                        </div>
                    ) : (
                        activeLevel === 0 ? renderDayView() : renderHorizonView()
                    )}
                </div>
            </div>
        </div>
    );
};

export default LifeOS;
